import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Divider,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  alpha
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
  Description as DescriptionIcon,
  Payment as PaymentIcon,
  Undo as UndoIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { supabase } from '../../../supabaseClient';

const PayableDetailDialog = ({ open, onClose, payable, onMarkPayment, onMarkUnpaid, canEdit = true, canDelete = true }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && payable?.invoice_file_path) {
      // Reset states when opening
      setLoading(true);
      setError(null);
      setInvoiceUrl(null);
      // Load invoice after a short delay to ensure dialog is mounted
      setTimeout(() => {
        loadInvoiceUrl();
      }, 100);
    }
    return () => {
      if (invoiceUrl) {
        URL.revokeObjectURL(invoiceUrl);
        setInvoiceUrl(null);
      }
    };
  }, [open, payable?.invoice_file_path]);

  const loadInvoiceUrl = async () => {
    if (!payable?.invoice_file_path) {
      setError('No invoice file path provided');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Determine the bucket based on transaction type with correct naming
      let bucketName;
      switch (payable.transaction_type) {
        case 'bottles':
          bucketName = 'bottles-transactions-invoices';
          break;
        case 'caps':
          bucketName = 'caps-transactions_invoices'; // Note: underscore for caps
          break;
        case 'labels':
          bucketName = 'labels-transactions-invoices';
          break;
        case 'raw_materials':
          bucketName = 'raw-materials-transactions-invoices'; // Note: hyphens for raw-materials
          break;
        case 'supplies':
          bucketName = 'supplies-transactions-invoices';
          break;
        case 'expense':
          bucketName = 'expenses-documents';
          break;
        case 'products':
          bucketName = 'products-inventory-invoice-documents';
          break;
        default:
          bucketName = `${payable.transaction_type}-transactions-invoices`;
      }
      
      // Download the file
      const { data, error: downloadError } = await supabase.storage
        .from(bucketName)
        .download(payable.invoice_file_path);

      if (downloadError) {
        console.error('Download error:', downloadError);
        throw new Error(downloadError.message || 'Failed to download invoice');
      }

      if (!data) {
        throw new Error('No data received from storage');
      }

      // Create a URL for the blob
      const url = URL.createObjectURL(data);
      setInvoiceUrl(url);
    } catch (err) {
      console.error('Error loading invoice:', err);
      setError(`Failed to load invoice: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!payable?.invoice_file_path) return;

    try {
      // Determine the bucket based on transaction type with correct naming
      let bucketName;
      switch (payable.transaction_type) {
        case 'bottles':
          bucketName = 'bottles-transactions-invoices';
          break;
        case 'caps':
          bucketName = 'caps-transactions_invoices'; // Note: underscore for caps
          break;
        case 'labels':
          bucketName = 'labels-transactions-invoices';
          break;
        case 'raw_materials':
          bucketName = 'raw-materials-transactions-invoices'; // Note: hyphens for raw-materials
          break;
        case 'supplies':
          bucketName = 'supplies-transactions-invoices';
          break;
        case 'expense':
          bucketName = 'expenses-documents';
          break;
        case 'products':
          bucketName = 'products-inventory-invoice-documents';
          break;
        default:
          bucketName = `${payable.transaction_type}-transactions-invoices`;
      }
      
      const { data, error } = await supabase.storage
        .from(bucketName)
        .download(payable.invoice_file_path);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = payable.invoice_file_name || 'invoice.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading invoice:', err);
      setError('Failed to download invoice');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMMM dd, yyyy');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'partial':
        return 'warning';
      case 'unpaid':
      case 'pending':
        return 'error';
      default:
        return 'default';
    }
  };

  if (!payable) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '95vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ReceiptIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={400}>
              Invoice Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {payable.invoice_number || 'No invoice number'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Status and Amount Summary */}
        <Paper sx={{ p: 3, mb: 3, mt: 3, background: alpha(theme.palette.primary.main, 0.03), border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                Payment Status
              </Typography>
              <Chip
                label={payable.payment_status?.toUpperCase() || 'UNPAID'}
                color={getStatusColor(payable.payment_status)}
                sx={{ fontWeight: 400, px: 2, py: 2.5 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Total Amount
              </Typography>
              <Typography variant="h6" fontWeight={400} color="primary.main">
                {formatCurrency(payable.total_amount)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Remaining Balance
              </Typography>
              <Typography variant="h6" fontWeight={400} color="error.main">
                {formatCurrency(payable.remaining_amount)}
              </Typography>
            </Grid>
          </Grid>

          {/* Payment Progress for Partial Payments */}
          {payable.paid_amount > 0 && payable.remaining_amount > 0 && (
            <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Payment Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1, height: 8, bgcolor: alpha(theme.palette.grey[300], 0.3), borderRadius: 1, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: `${(payable.paid_amount / payable.total_amount) * 100}%`,
                      bgcolor: 'success.main',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
                <Typography variant="body2" fontWeight={400} color="text.secondary">
                  {((payable.paid_amount / payable.total_amount) * 100).toFixed(1)}% paid
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" color="success.main">
                  Paid: {formatCurrency(payable.paid_amount)}
                </Typography>
                <Typography variant="caption" color="error.main">
                  Remaining: {formatCurrency(payable.remaining_amount)}
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Vendor Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={400} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon fontSize="small" />
            Vendor Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Vendor Name
              </Typography>
              <Typography variant="body2" fontWeight={400}>
                {payable.supplier_name || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Category
              </Typography>
              <Typography variant="body2" fontWeight={400}>
                {payable.transaction_type === 'raw_materials' ? 'Chemicals' : payable.transaction_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Invoice Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={400} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayIcon fontSize="small" />
            Invoice Dates
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Invoice Date
              </Typography>
              <Typography variant="body2" fontWeight={400}>
                {formatDate(payable.invoice_date)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Due Date
              </Typography>
              <Typography variant="body2" fontWeight={400}>
                {formatDate(payable.invoice_due_date)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Invoice Number
              </Typography>
              <Typography variant="body2" fontWeight={400}>
                {payable.invoice_number || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Item Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={400} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon fontSize="small" />
            Item Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Item Name
              </Typography>
              <Typography variant="body2" fontWeight={400}>
                {payable.item_name || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Quantity
              </Typography>
              <Typography variant="body2" fontWeight={400}>
                {payable.quantity || 0}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" color="text.secondary">
                Unit Cost
              </Typography>
              <Typography variant="body2" fontWeight={400}>
                {formatCurrency(payable.unit_cost)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Payment History */}
        {payable.paid_amount > 0 && payable.notes && (() => {
          try {
            const paymentHistory = JSON.parse(payable.notes);
            if (Array.isArray(paymentHistory) && paymentHistory.some(entry => entry.amount)) {
              return (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={400} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoneyIcon fontSize="small" />
                    Payment History
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1.5}>
                    {paymentHistory.filter(entry => entry.amount).map((payment, index) => (
                      <Paper 
                        key={index}
                        sx={{ 
                          p: 2, 
                          backgroundColor: alpha(theme.palette.success.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">Amount Paid:</Typography>
                              <Typography variant="body2" fontWeight={400} color="success.main">
                                {formatCurrency(payment.amount)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="text.secondary">Date:</Typography>
                              <Typography variant="body2" fontWeight={400}>
                                {payment.date ? formatDate(payment.date) : 'N/A'}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">Method:</Typography>
                              <Typography variant="body2" fontWeight={400}>
                                {payment.method ? payment.method.charAt(0).toUpperCase() + payment.method.slice(1).replace('_', ' ') : 'N/A'}
                              </Typography>
                            </Box>
                            {payment.reference && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" color="text.secondary">Reference:</Typography>
                                <Typography variant="body2" fontWeight={400}>
                                  {payment.reference}
                                </Typography>
                              </Box>
                            )}
                          </Grid>
                          {payment.notes && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Notes:</Typography>
                              <Typography variant="body2" fontWeight={400} sx={{ mt: 0.5 }}>
                                {payment.notes}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              );
            }
          } catch (e) {
            return null;
          }
          return null;
        })()}

        {/* Amount Breakdown */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={400} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoneyIcon fontSize="small" />
            Amount Breakdown
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Subtotal</Typography>
              <Typography variant="body2" fontWeight={400}>{formatCurrency(payable.subtotal_amount)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Tax</Typography>
              <Typography variant="body2" fontWeight={400}>{formatCurrency(payable.tax_amount)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Shipping</Typography>
              <Typography variant="body2" fontWeight={400}>{formatCurrency(payable.shipping_cost)}</Typography>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight={400}>Total</Typography>
              <Typography variant="body1" fontWeight={400} color="primary.main">
                {formatCurrency(payable.total_amount)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="success.main">Paid Amount</Typography>
              <Typography variant="body2" fontWeight={400} color="success.main">
                {formatCurrency(payable.paid_amount)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight={400} color="error.main">Remaining Balance</Typography>
              <Typography variant="body1" fontWeight={400} color="error.main">
                {formatCurrency(payable.remaining_amount)}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Invoice Document */}
        {payable.invoice_file_path && (
          <Box>
            <Typography variant="subtitle2" fontWeight={400} gutterBottom>
              Invoice Document
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={40} />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
                <Button
                  size="small"
                  onClick={loadInvoiceUrl}
                  sx={{ ml: 2 }}
                >
                  Retry
                </Button>
              </Alert>
            ) : invoiceUrl ? (
              <Box>
                <Box sx={{ 
                  width: '100%',
                  height: '700px',
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: '#525252'
                }}>
                  <iframe
                    src={invoiceUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      display: 'block'
                    }}
                    title="Invoice Document"
                  />
                </Box>
                <Button
                  startIcon={<DownloadIcon />}
                  variant="outlined"
                  onClick={handleDownloadInvoice}
                  sx={{ mt: 2 }}
                  fullWidth
                >
                  Download Invoice
                </Button>
              </Box>
            ) : (
              <Alert severity="info">Loading invoice document...</Alert>
            )}
          </Box>
        )}
        {!payable.invoice_file_path && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No invoice document attached to this transaction
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`, display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          {payable.paid_amount > 0 && canEdit && (
            <Button 
              onClick={() => {
                onMarkUnpaid(payable);
                onClose();
              }} 
              color="error"
              variant="outlined"
              startIcon={<UndoIcon />}
              sx={{ mr: 1 }}
            >
              Reverse Payment
            </Button>
          )}
          {payable.payment_status !== 'paid' && canEdit && (
            <Button 
              onClick={() => {
                onMarkPayment(payable);
                onClose();
              }} 
              color="success"
              variant="contained"
              startIcon={<PaymentIcon />}
            >
              Mark Payment
            </Button>
          )}
        </Box>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayableDetailDialog;
